import { useDrawingViewer } from "@/domains/documents/hooks/useDrawingViewer";
import { useIfcDiffOverview } from "@/domains/documents/hooks/useIfcDiffOverview";
import { useMemo } from "react";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTableDocumentKinds } from "./useTableDocumentKinds";
import type { useTableSigningState } from "./useTableSigningState";
type Options = Pick<ReturnType<typeof documentTableOptions>, "store" | "buildingId"> &
  Pick<ReturnType<typeof useTableDocumentKinds>, "displayDocuments"> &
  Pick<ReturnType<typeof useTableDialogState>, "versionDocsCache"> &
  Pick<ReturnType<typeof useTableSigningState>, "canViewDrawingCompareTool">;
export function useTableCompare(options: Options) {
  const { store, displayDocuments, versionDocsCache, canViewDrawingCompareTool, buildingId } =
    options;
  // Get selected PDF documents for comparison feature. Also includes older
  // versions cached in versionDocsCache, since those rows aren't in the MST
  // store or the documents prop but can still be checkbox-selected.
  const selectedPdfs = selectedPdfDocuments(options);

  // Can compare when exactly 2 PDFs are selected
  const canComparePdfs = selectedPdfs.length === 2;

  // WHA-3695: differences-overview action + modal state. Candidates include the
  // older versions cached outside the store (lazily loaded on version-group
  // expansion), so a selected superseded revision is still resolvable — and so
  // the hook can see a v1's siblings when deciding whether to badge it.
  const ifcDiffCandidates = useMemo(
    () => [...displayDocuments, ...[...versionDocsCache.values()].flatMap((entry) => entry.docs)],
    [displayDocuments, versionDocsCache]
  );
  const { canCompareIfcs, handleDiffOverview, diffOverview, closeDiffOverview } =
    useIfcDiffOverview(ifcDiffCandidates, store ? (id) => store.isSelected(String(id)) : undefined);

  // DWG "View drawing" / "Compare drawings" — same candidate set, selection
  // read from the store. The hook orders a compare pair older-first before it
  // writes `?ids=`, so tick order never decides which side is the diff's base.
  const {
    canUseDrawingViewer,
    canViewDrawing,
    canCompareDrawings,
    openDrawingViewer,
    handleViewSelectedDrawing,
    handleCompareSelectedDrawings,
  } = useDrawingViewer(ifcDiffCandidates, store?.selectedDocumentIds, {
    // Staff-only for now — matches the AdminProtectedRoute on every Drawings
    // route (customer rollout later swaps to VIEW_DRAWING_COMPARE_TOOL).
    enabled: canViewDrawingCompareTool,
    buildingId,
  });
  return {
    selectedPdfs,
    canComparePdfs,
    canCompareIfcs,
    canCompareDrawings,
    handleDiffOverview,
    handleCompareSelectedDrawings,
    canUseDrawingViewer,
    openDrawingViewer,
    diffOverview,
    closeDiffOverview,
    canViewDrawing,
    handleViewSelectedDrawing,
  };
}

function selectedPdfDocuments({ store, displayDocuments, versionDocsCache }: Options) {
  if (!store) return [];
  const selected = displayDocuments.filter(
    (doc) => store.isSelected(doc.id) && doc.content_type === "application/pdf"
  );
  const seen = new Set(selected.map((d) => d.id));
  for (const entry of versionDocsCache.values()) {
    for (const v of entry.docs) {
      if (!seen.has(v.id) && store.isSelected(v.id) && v.content_type === "application/pdf") {
        seen.add(v.id);
        selected.push(v);
      }
    }
  }
  return selected;
}
